const IFRAME_SCROLL_REQUEST_MESSAGE_TYPE = "trial_finder_iframe_scroll_request";
const IFRAME_EMBEDDED_CLASS = "is-embedded-app";
const IFRAME_MODAL_OFFSET_CLASS = "has-iframe-modal-offset";
const IFRAME_MODAL_OFFSET_VAR = "--iframe-modal-offset-top";
const IFRAME_MODAL_OFFSET_MESSAGE_TYPE = "trial_finder_iframe_offset";
const IFRAME_WINDOW_HEIGHT_TYPE = "trial_finder_window_height";

type OutboundMessage = string | Record<string, unknown>;

type IframeScrollRequestMessage = {
    type: typeof IFRAME_SCROLL_REQUEST_MESSAGE_TYPE;
    deltaY?: number;
    targetTop?: number;
    targetHeight?: number;
    block?: ScrollLogicalPosition;
    behavior?: ScrollBehavior;
};

type IframeOffsetMessage = {
    type: typeof IFRAME_MODAL_OFFSET_MESSAGE_TYPE;
    iframeViewportTop?: number;
};

type IframeWindowHeightMessage = {
    type: typeof IFRAME_WINDOW_HEIGHT_TYPE;
    windowHeight?: number;
};

type ParentScrollListenerOptions = {
    allowedOrigins?: string[];
};

function isInIframe() {
    if (typeof window === "undefined") {
        return false;
    }

    return window.self !== window.top;
}

function syncIframeDocumentState() {
    if (typeof document === "undefined") {
        return;
    }

    const root = document.documentElement;
    root.classList.toggle(IFRAME_EMBEDDED_CLASS, isInIframe());
}

function sendMessage(msg: OutboundMessage, targetOrigin = "*") {
    if (typeof window === "undefined" || !window.parent || window.parent === window) {
        return;
    }

    window.parent.postMessage(msg, targetOrigin);
}

function applyIframeModalOffset(rawOffset: unknown) {
    if (typeof document === "undefined") {
        return;
    }

    const root = document.documentElement;
    const numericOffset =
        typeof rawOffset === "number" ? rawOffset : typeof rawOffset === "string" ? Number(rawOffset) : NaN;

    if (!Number.isFinite(numericOffset)) {
        return;
    }

    const offset = Math.max(0, Math.round(numericOffset));
    root.style.setProperty(IFRAME_MODAL_OFFSET_VAR, `${offset}px`);
    root.classList.add(IFRAME_MODAL_OFFSET_CLASS);
}

function applyParentWindowHeight(height: number) {
    if (typeof document === "undefined") {
        return;
    }

    const root = document.documentElement;
    root.style.setProperty("--parent-window-height", `${height}px`);
}
function parseParentWindowHeight(data: unknown) {
    if (!data || typeof data !== "object") {
        return null;
    }

    const payload = data as IframeWindowHeightMessage;
    if (payload.type !== IFRAME_WINDOW_HEIGHT_TYPE) {
        return null;
    }

    return payload.windowHeight;
}

function parseIframeOffsetMessage(data: unknown) {
    if (!data || typeof data !== "object") {
        return null;
    }

    const payload = data as IframeOffsetMessage;
    if (payload.type !== IFRAME_MODAL_OFFSET_MESSAGE_TYPE) {
        return null;
    }

    return payload.iframeViewportTop;
}

function listenForIframeModalOffset() {
    if (!isInIframe()) {
        return () => {
            /* no-op */
        };
    }

    const onMessage = (event: MessageEvent) => {
        if (event.source !== window.parent) {
            return;
        }

        const offset = parseIframeOffsetMessage(event.data);
        if (offset === null) {
            return;
        }

        applyIframeModalOffset(offset);
    };

    window.addEventListener("message", onMessage);

    return () => {
        window.removeEventListener("message", onMessage);
    };
}

function listenForIframeParentWindowResize() {
    if (!isInIframe()) {
        return () => {
            /* no-op */
        };
    }

    const onMessage = (event: MessageEvent) => {
        if (event.source !== window.parent) {
            return;
        }
        const windowHeight = parseParentWindowHeight(event.data);
        if (windowHeight !== null && typeof windowHeight === "number") {
            applyParentWindowHeight(windowHeight);
        }
    };

    window.addEventListener("message", onMessage);

    return () => {
        window.removeEventListener("message", onMessage);
    };
}

function getScrollDelta(targetTop: number, targetHeight: number, viewportHeight: number, block: ScrollLogicalPosition) {
    switch (block) {
        case "center":
            return targetTop - (viewportHeight - targetHeight) / 2;
        case "end":
            return targetTop + targetHeight - viewportHeight;
        case "start":
        case "nearest":
        default:
            return targetTop;
    }
}

function isIframeScrollRequestMessage(data: unknown): data is IframeScrollRequestMessage {
    if (!data || typeof data !== "object") {
        return false;
    }

    const message = data as Partial<IframeScrollRequestMessage>;
    if (message.type !== IFRAME_SCROLL_REQUEST_MESSAGE_TYPE) {
        return false;
    }

    const hasDeltaY = typeof message.deltaY === "number" && Number.isFinite(message.deltaY);
    const hasTargetTop = typeof message.targetTop === "number" && Number.isFinite(message.targetTop);
    return hasDeltaY || hasTargetTop;
}

function getIframeFromMessageSource(source: MessageEventSource | null) {
    if (typeof document === "undefined") {
        return null;
    }

    const iframes = document.querySelectorAll("iframe");
    for (const iframe of iframes) {
        if (iframe.contentWindow === source) {
            return iframe;
        }
    }

    return null;
}

function isAllowedOrigin(origin: string, allowedOrigins: string[]) {
    if (allowedOrigins.length === 0) {
        return true;
    }

    return allowedOrigins.includes(origin);
}

function listenForIframeScrollRequests(options: ParentScrollListenerOptions = {}) {
    if (typeof window === "undefined") {
        return () => {
            /* no-op */
        };
    }

    const allowedOrigins = options.allowedOrigins ?? [];

    const onMessage = (event: MessageEvent) => {
        if (!isAllowedOrigin(event.origin, allowedOrigins)) {
            return;
        }

        const iframe = getIframeFromMessageSource(event.source);
        if (!iframe) {
            return;
        }

        if (!isIframeScrollRequestMessage(event.data)) {
            return;
        }

        const behavior = event.data.behavior ?? "smooth";
        let deltaY: number | undefined;

        // Prefer absolute target coordinates from the iframe to avoid viewport mismatch issues.
        if (typeof event.data.targetTop === "number" && Number.isFinite(event.data.targetTop)) {
            const block = event.data.block ?? "start";
            const targetHeight =
                typeof event.data.targetHeight === "number" && Number.isFinite(event.data.targetHeight)
                    ? event.data.targetHeight
                    : 0;
            const absoluteTargetTop = iframe.getBoundingClientRect().top + event.data.targetTop;
            deltaY = getScrollDelta(absoluteTargetTop, targetHeight, window.innerHeight, block);
        } else if (Number.isFinite(event.data.deltaY)) {
            deltaY = event.data.deltaY;
        }

        if (deltaY === undefined) {
            return;
        }

        window.scrollBy({
            top: Math.round(deltaY),
            behavior,
        });
    };

    window.addEventListener("message", onMessage);

    return () => {
        window.removeEventListener("message", onMessage);
    };
}

function scrollElementIntoView(target: Element | null, options: ScrollIntoViewOptions = {}) {
    if (!target) {
        return;
    }

    const behavior = options.behavior ?? "smooth";
    const block = options.block ?? "start";
    const inline = options.inline ?? "nearest";

    if (!isInIframe()) {
        target.scrollIntoView({ behavior, block, inline });
        return;
    }

    const targetRect = target.getBoundingClientRect();
    const message: IframeScrollRequestMessage = {
        type: IFRAME_SCROLL_REQUEST_MESSAGE_TYPE,
        // Fallback for parents that only read deltaY.
        deltaY: Math.round(getScrollDelta(targetRect.top, targetRect.height, window.innerHeight, block)),
        targetTop: targetRect.top,
        targetHeight: targetRect.height,
        block,
        behavior,
    };

    sendMessage(message);
}

export {
    sendMessage,
    isInIframe,
    syncIframeDocumentState,
    scrollElementIntoView,
    listenForIframeScrollRequests,
    listenForIframeModalOffset,
    listenForIframeParentWindowResize,
    IFRAME_SCROLL_REQUEST_MESSAGE_TYPE,
    IFRAME_MODAL_OFFSET_CLASS,
    IFRAME_MODAL_OFFSET_MESSAGE_TYPE,
};
export type { IframeScrollRequestMessage };
